/**
 * Shared user-facing copy and formatting helpers for error states.
 *
 * Multiple components render the same shape of empty/error state — a friendly
 * guidance sentence followed by the underlying technical error in parentheses
 * for support context. Centralizing the wording keeps the UX consistent and
 * avoids drift between e.g. `QueryBoundary` and `CommandCenterErrorPage`.
 */

/** Generic fallback used by `QueryBoundary` and any consumer that doesn't have
 *  a more specific context. */
export const DEFAULT_GENERIC_ERROR_SUBTITLE =
  'Something went wrong while loading this page. Please try refreshing. ' +
  'If the issue continues, contact your OpenGov administrator for assistance.';

/** Command Center–specific subtitle. Title already names the surface, so this
 *  reuses the generic try-refresh / contact-admin guidance with light tailoring. */
export const COMMAND_CENTER_GENERIC_ERROR_SUBTITLE =
  'Your session has expired due to inactivity. Please log in again to continue or please try refreshing the page. ';

const UNKNOWN_ERROR_FALLBACK = 'An unexpected error occurred';

/**
 * Compose the final user-facing subtitle: a friendly guidance message followed
 * by the underlying technical error in parentheses.
 *
 * Example output:
 *   "Something went wrong while loading this page. Please try refreshing.
 *    If the issue continues, contact your OpenGov administrator for assistance.
 *    (Error: Request failed 403: Access Denied)"
 */
export function formatErrorSubtitle(
  friendlyMessage: string,
  error: { message?: string } | unknown
): string {
  const technical =
    (typeof error === 'object' && error !== null && 'message' in error
      ? (error as { message?: string }).message
      : undefined) || UNKNOWN_ERROR_FALLBACK;
  return `${friendlyMessage} (Error: ${technical})`;
}
